// components/CaseView/Files.tsx
import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core";
import CustomLoader from "../../components/CustomLoader";
import Dialog from "@material-ui/core/Dialog";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
// @ts-ignore: react-pdf has no TypeScript declarations
import { Document, Page, pdfjs } from "react-pdf";
import { FileUploader } from "react-drag-drop-files";
import ConfirmationPopup from "../../components/ConfirmationPopup";
import Loader from "react-loader-spinner";
import { useStore } from "../../context/StoreContext";
import { useFiles } from "../../hooks/CaseView/useFiles";
import "./Files.css";
import BottomDrawer from "../../components/shared/BottomDrawer";
import { Typography } from "@mui/material";

import FilesTable from "../../components/CaseView/Files/FilesTable";
import { useFileOperations } from "../../hooks/CaseView/useFileOperations";
import {
  PrimaryButton,
  StyledIconButton,
} from "../../components/shared/helpers/GlobalStylesv5";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { tokens } from "../../theme/tokens";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const useStyle = makeStyles(() => ({
  pageWrap: { padding: tokens.space[4] },
  card: {
    backgroundColor: tokens.color.surface.default,
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.md,
    overflow: "hidden",
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[2],
    padding: `${tokens.space[3]} ${tokens.space[4]}`,
    borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  },
  cardHeadIcon: {
    color: tokens.color.brand.primaryAction,
    display: "inline-flex",
    "& svg": { fontSize: "1.125rem" },
  },
  cardTitle: {
    margin: 0,
    fontSize: "0.9375rem",
    fontWeight: tokens.type.denseStrong.weight,
    color: tokens.color.text.primary,
  },
  count: {
    marginLeft: "auto",
    fontSize: tokens.type.caption.size,
    fontWeight: tokens.type.denseStrong.weight,
    color: tokens.color.text.secondary,
  },
  cardBody: {
    padding: tokens.space[4],
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[4],
  },
  dropZone: {
    display: "block",
    width: "100%",
    maxWidth: "none",
    border: `${tokens.borderWidth} dashed ${tokens.color.border.strong}`,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surface.sunken,
    padding: `${tokens.space[6]} ${tokens.space[4]}`,
    cursor: "pointer",
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}, background-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": {
      borderColor: tokens.color.brand.primary,
      backgroundColor: tokens.color.surface.hover,
    },
  },
  dropInner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.space[2],
    textAlign: "center",
    pointerEvents: "none",
  },
  dropIcon: { fontSize: "40px", color: tokens.color.brand.primaryAction },
  dropTitle: {
    fontSize: tokens.type.body.size,
    fontWeight: tokens.type.bodyStrong.weight,
    color: tokens.color.text.primary,
  },
  dropSub: {
    fontSize: tokens.type.caption.size,
    color: tokens.color.text.secondary,
  },
}));

interface FilesProps {
  case_id: string;
}

function Files({ case_id }: FilesProps) {
  const classes = useStyle();
  // Context store
  const { uploadLoading } = useStore();

  // Custom hooks
  const { files, isDeleting, deleteFile, uploadFiles, fetchFiles } =
    useFiles(case_id);
  const fileOps = useFileOperations(case_id);

  // Local UI state
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  // PDF viewer state
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<any>(null);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Refresh files list when all uploads are complete
  useEffect(() => {
    const allComplete = uploadLoading.uploads.every(
      (upload) => upload.uploaded || upload.error,
    );
    const hasUploads = uploadLoading.uploads.length > 0;
    const someSuccessful = uploadLoading.uploads.some(
      (upload) => upload.uploaded,
    );

    if (hasUploads && allComplete && someSuccessful) {
      // Delay refresh slightly to allow backend processing
      const timer = setTimeout(() => {
        fetchFiles();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [uploadLoading.uploads, fetchFiles]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    window.scrollTo(0, document.body.scrollHeight);
  }

  // Navigate to next page
  const goToNextPage = useCallback(() => {
    if (numPages && pageNumber < numPages) {
      setPageNumber((prev) => prev + 1);
    }
  }, [pageNumber, numPages]);

  // Navigate to previous page
  const goToPreviousPage = useCallback(() => {
    if (pageNumber > 1) {
      setPageNumber((prev) => prev - 1);
    }
  }, [pageNumber]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only handle arrow keys when PDF is open
      if (!selectedFile || !pdfData) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPreviousPage();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToNextPage();
      } else if (e.key === "Escape") {
        handleDrawerClose();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [selectedFile, pdfData, goToNextPage, goToPreviousPage]);

  // Handle multiple file uploads
  const handleFileUpload = async (filesInput: File | FileList | File[]) => {
    let fileArray: File[] = [];

    if (filesInput instanceof FileList) {
      // Convert FileList to Array
      fileArray = Array.from(filesInput);
    } else if (Array.isArray(filesInput)) {
      fileArray = filesInput;
    } else {
      // Single file
      fileArray = [filesInput];
    }

    await uploadFiles(fileArray);
  };

  // Handle file deletion
  const handleDeleteClick = (file: any) => {
    setFileToDelete(file);
    setDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (fileToDelete) {
      const success = await deleteFile(fileToDelete.key);
      if (success) {
        setDeleteDialog(false);
        setFileToDelete(null);
      }
    }
  };

  useEffect(() => {
    if (selectedFile && pdfData) {
      setOpenDrawer(true);
    }
  }, [selectedFile, pdfData]);

  const handleFileClick = async (e: React.MouseEvent, file: any) => {
    if (file.type === "doc") {
      const pdfUrl = await fileOps.getFile(file.name);

      if (pdfUrl) {
        setPdfData(pdfUrl);
        setPageNumber(1);
        setSelectedFile(file.name);
      }
    } else if (file.type === "img") {
      fileOps.openFileInNewTab(file.name, "image/jpeg");
    } else if (file.type === "video/mp4") {
      fileOps.openFileInNewTab(file.name, "video/mp4");
    } else {
      fileOps.openFileInNewTab(file.name, "other");
    }
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedFile(null);
    setPdfData(null);
  };

  const isUploading = uploadLoading.uploads.some(
    (upload) => !upload.uploaded && !upload.error,
  );

  const fileCount =
    files?.filter((file) => file.type !== "video").length ?? 0;

  return (
    <div className={classes.pageWrap}>
      <div className={classes.card}>
        <div className={classes.cardHead}>
          <span className={classes.cardHeadIcon}>
            <FolderOutlinedIcon />
          </span>
          <h5 className={classes.cardTitle}>Documents</h5>
          <span className={classes.count}>
            {fileCount} {fileCount === 1 ? "file" : "files"}
          </span>
        </div>

        <div className={classes.cardBody}>
          {/* Drop zone */}
          <FileUploader
            handleChange={handleFileUpload}
            name="file"
            multiple={true}
            disabled={isUploading || isDeleting}
            classes={classes.dropZone}
          >
            <div className={classes.dropInner}>
              {isUploading || isDeleting ? (
                <>
                  <Loader
                    type="Puff"
                    color={tokens.color.brand.primary}
                    height={40}
                    width={40}
                  />
                  <div className={classes.dropTitle}>Uploading…</div>
                </>
              ) : (
                <>
                  <CloudUploadOutlinedIcon className={classes.dropIcon} />
                  <div className={classes.dropTitle}>
                    Drag &amp; drop files here
                  </div>
                  <div className={classes.dropSub}>or click to browse</div>
                </>
              )}
            </div>
          </FileUploader>

          {/* Files Table */}
          <FilesTable
            files={files}
            onFileClick={handleFileClick}
            onDeleteClick={handleDeleteClick}
          />
        </div>
      </div>

      {/* PDF Viewer */}
      <BottomDrawer
        open={openDrawer}
        onClose={() => handleDrawerClose()}
        title={
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {selectedFile}
          </Typography>
        }
        actionButton={
          <PrimaryButton
            onClick={() => {
              if (!selectedFile) return;
              fileOps.downloadFile(selectedFile);
            }}
            style={{
              cursor: "pointer",
            }}
            disabled={fileOps.loading}
          >
            {fileOps.loading ? "Downloading..." : "Download file"}
          </PrimaryButton>
        }
      >
        <div
          style={{
            minHeight: "97vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: 1 }}>
            <Document
              style={{
                display: "flex",
                justifyContent: "center",
                textAlign: "center",
              }}
              file={{ url: pdfData }}
              onLoadSuccess={onDocumentLoadSuccess}
            >
              <Page pageNumber={pageNumber} />
            </Document>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: tokens.space[3],
                padding: `${tokens.space[3]} 0`,
                position: "sticky",
                bottom: 0,
                backgroundColor: tokens.color.surface.default,
                borderTop: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
              }}
            >
              <StyledIconButton
                onClick={() => {
                  setPageNumber(pageNumber - 1);
                  window.scrollTo(0, document.body.scrollHeight);
                }}
                disabled={pageNumber <= 1}
                aria-label="Previous page"
              >
                <ChevronLeftIcon />
              </StyledIconButton>
              <span
                style={{
                  fontSize: tokens.type.dense.size,
                  color: tokens.color.text.secondary,
                  fontVariantNumeric: "tabular-nums",
                  minWidth: "110px",
                  textAlign: "center",
                }}
              >
                Page {pageNumber} of {numPages ?? "…"}
              </span>
              <StyledIconButton
                onClick={() => {
                  setPageNumber(pageNumber + 1);
                  window.scrollTo(0, document.body.scrollHeight);
                }}
                disabled={numPages !== null && pageNumber >= numPages}
                aria-label="Next page"
              >
                <ChevronRightIcon />
              </StyledIconButton>
            </div>
          </div>
        </div>
      </BottomDrawer>

      {/* Delete Confirmation */}
      <ConfirmationPopup
        text="Are you sure you want to delete this document?"
        state={deleteDialog}
        setHandleClose={() => setDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
      />
      {/* Loading Dialog */}
      <Dialog open={fileOps.loading}>
        {fileOps.loading && <CustomLoader />}
      </Dialog>
    </div>
  );
}

export default Files;
